import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import './Services.css';

const Services = () => {
  const navigate = useNavigate();
  const { isAuthenticated, pendingRental, savePendingRental, clearPendingRental } = useAuth();

  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [countries, setCountries] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [sortFilter, setSortFilter] = useState('all');
  const [renting, setRenting] = useState(false);
  const [error, setError] = useState('');
  const [popupError, setPopupError] = useState('');
  const [balance, setBalance] = useState(null);
  const hasFetchedBalance = useRef(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await api.get('/v1/services');
        if (response?.data?.services) {
          setServices(response.data.services);
        }
      } catch (error) {
        console.error('Failed to fetch services:', error);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (!isAuthenticated() || hasFetchedBalance.current) return;
    hasFetchedBalance.current = true;

    const fetchBalance = async () => {
      try {
        const response = await api.get('/v1/users', true);
        const userData = response.data?.user || response.data;

        if (userData?.coins) {
          setBalance({ coins: userData.coins, frozenCoins: userData.frozenCoins });
        }
      } catch (err) {
        console.error('Failed to fetch balance:', err);
      }
    };
    fetchBalance();
  }, [isAuthenticated()]);

  // Handle pending rental after login
  useEffect(() => {
    if (isAuthenticated() && pendingRental) {
      const { service, country } = pendingRental;

      // Find the service and country in our lists
      const foundService = services.find(s => s.slug === service.slug);
      if (foundService) {
        setSelectedService(foundService);

        // Fetch countries for the service
        const fetchCountriesAndSelect = async () => {
          try {
            const response = await api.get(`/v1/services/countries/slug?slug=${service.slug}`);
            if (response?.data?.serviceCountries) {
              setCountries(response.data.serviceCountries);
              const foundCountry = response.data.serviceCountries.find(
                c => c.countryCode === country.countryCode
              );
              if (foundCountry) {
                setSelectedCountry(foundCountry);
                // Auto-trigger the rental
                handleReceiveSMS(foundService, foundCountry);
              }
            }
          } catch (error) {
            console.error('Failed to restore pending rental:', error);
          }
        };

        fetchCountriesAndSelect();
      }
      clearPendingRental();
    }
  }, [isAuthenticated, pendingRental, services]);

  const handleServiceClick = async (service) => {
    setSelectedService(service);
    setSelectedCountry(null);
    setCountrySearch('');
    setError('');
    setLoadingCountries(true);
    setCountries([]);
    try {
      const response = await api.get(`/v1/services/countries/slug?slug=${service.slug}`);
      if (response?.data?.serviceCountries) {
        setCountries(response.data.serviceCountries);
      }
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    } finally {
      setLoadingCountries(false);
    }
  };

  const handleCountryClick = (country) => {
    setSelectedCountry(country);
    setError('');
  };

  const handleReceiveSMS = async (service = selectedService, country = selectedCountry) => {
    if (!service || !country) return;

    // Check if user is authenticated
    if (!isAuthenticated()) {
      // Save pending rental and redirect to auth
      savePendingRental({
        service: {
          slug: service.slug,
          name: service.name,
        },
        country: {
          countryCode: country.countryCode,
          country: country.country,
          phoneCode: country.phoneCode,
          price: country.price,
        },
      });
      navigate('/auth');
      return;
    }

    // User is authenticated, proceed with rental
    setRenting(true);
    setError('');

    try {
      const response = await api.post(
        `/v1/users/rentals/rent?slug=${service.slug}&phoneCode=${country.phoneCode}&countryCode=${country.countryCode}`,
        null,
        true // authenticated request
      );

      if (response) {
        // Navigate to verification page with rental data
        navigate('/verification', {
          state: {
            rental: response,
            serviceLogoUrl: service.logoUrl,
            countryFlagUrl: country.countryFlagUrl,
            countryName: country.country,
          },
        });
      }
    } catch (err) {
      const errorMsg = err.data?.message || err.message || '';
      if (errorMsg.includes('err_something_went_wrong')) {
        setPopupError('Something went wrong. Please try a little bit later or try another country.');
        setTimeout(() => setPopupError(''), 5000);
      } else {
        setError(errorMsg || 'Failed to rent number. Please try again.');
      }
    } finally {
      setRenting(false);
    }
  };

  const filteredServices = useMemo(() => {
    if (!serviceSearch.trim()) return services;
    return services.filter(service =>
      service.name.toLowerCase().includes(serviceSearch.toLowerCase())
    );
  }, [services, serviceSearch]);

  const filteredCountries = useMemo(() => {
    let filtered = countries;

    if (countrySearch.trim()) {
      filtered = filtered.filter(country =>
        country.country.toLowerCase().includes(countrySearch.toLowerCase())
      );
    }

    if (sortFilter === 'cheapest') {
      filtered = [...filtered].sort((a, b) => a.price - b.price);
    } else if (sortFilter === 'fastest') {
      filtered = [...filtered].sort((a, b) => b.count - a.count);
    }

    return filtered;
  }, [countries, countrySearch, sortFilter]);

  return (
    <div className="services-dashboard">
      {popupError && (
        <div className="error-popup">
          <div className="error-popup-content">
            <span className="material-symbols-outlined error-popup-icon">error</span>
            <p className="error-popup-message">{popupError}</p>
            <button className="error-popup-close" onClick={() => setPopupError('')}>
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
        </div>
      )}
      <div className="dashboard-container">
        {/* Services Panel */}
        <section className="panel services-panel">
          <div className="panel-header">
            <div className="panel-title-row">
              <h3 className="panel-title">Select Service</h3>
              <span className="panel-badge">{services.length}+ Services</span>
            </div>
            <div className="search-wrapper">
              <span className="material-symbols-outlined search-icon">search</span>
              <input
                type="text"
                className="search-input"
                placeholder="Search service..."
                value={serviceSearch}
                onChange={(e) => setServiceSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="panel-content custom-scrollbar">
            {loadingServices ? (
              <div className="loading-state">Loading services...</div>
            ) : filteredServices.length === 0 ? (
              <div className="empty-state">No services found</div>
            ) : (
              filteredServices.map((service) => (
                <div
                  key={service._id}
                  className={`service-item ${selectedService?._id === service._id ? 'selected' : ''}`}
                  onClick={() => handleServiceClick(service)}
                >
                  <div className="service-icon">
                    <img
                      src={service.logoUrl}
                      alt={service.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <span className="material-symbols-outlined fallback-icon" style={{ display: 'none' }}>
                      apps
                    </span>
                  </div>
                  <p className="service-name">{service.name}</p>
                </div>
              ))
            )}
          </div>
        </section>

        {/* Countries Panel */}
        <section className="panel countries-panel">
          <div className="panel-header">
            <div className="panel-title-row">
              <h3 className="panel-title">
                {selectedService
                  ? `Select Country for ${selectedService.name}`
                  : 'Select a Service First'
                }
              </h3>
              {selectedService && (
                <div className="filter-buttons">
                  <button
                    className={`filter-btn ${sortFilter === 'all' ? 'active' : ''}`}
                    onClick={() => setSortFilter('all')}
                  >
                    All
                  </button>
                  <button
                    className={`filter-btn ${sortFilter === 'fastest' ? 'active' : ''}`}
                    onClick={() => setSortFilter('fastest')}
                  >
                    Fastest
                  </button>
                  <button
                    className={`filter-btn ${sortFilter === 'cheapest' ? 'active' : ''}`}
                    onClick={() => setSortFilter('cheapest')}
                  >
                    Cheapest
                  </button>
                </div>
              )}
            </div>
            {selectedService && (
              <div className="search-wrapper">
                <span className="material-symbols-outlined search-icon">search</span>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search country..."
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className={`panel-content custom-scrollbar ${selectedCountry ? 'has-footer' : ''}`}>
            {!selectedService ? (
              <div className="empty-state">
                <span className="material-symbols-outlined empty-icon">touch_app</span>
                <p>Select a service from the left panel to view available countries</p>
              </div>
            ) : loadingCountries ? (
              <div className="loading-state">Loading countries...</div>
            ) : filteredCountries.length === 0 ? (
              <div className="empty-state">No countries available</div>
            ) : (
              filteredCountries.map((country) => (
                <div
                  key={country._id}
                  className={`country-item ${selectedCountry?._id === country._id ? 'selected' : ''}`}
                  onClick={() => handleCountryClick(country)}
                >
                  <div className="country-info">
                    <div className="country-flag">
                      <img src={country.countryFlagUrl} alt={country.country} />
                    </div>
                    <p className="country-name">{country.country}</p>
                  </div>
                  <div className="country-action">
                    <p className={`country-price ${selectedCountry?._id === country._id ? 'selected' : ''}`}>
                      ${(country.price / 100).toFixed(2)}
                    </p>
                    {selectedCountry?._id === country._id ? (
                      <div className="check-icon">
                        <span className="material-symbols-outlined">check</span>
                      </div>
                    ) : (
                      <span className="material-symbols-outlined chevron-icon">chevron_right</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Bottom Action Bar */}
          {selectedService && selectedCountry && (
            <div className="action-bar">
              <div className="selection-info">
                <span className="selection-label">Selected Plan</span>
                <p className="selection-text">
                  <span className="highlight">{selectedService.name}</span>
                  <span className="separator">in</span>
                  <span className="highlight">{selectedCountry.country}</span>
                </p>
                {error && (
                  <p className="action-error">{error}</p>
                )}
              </div>
              <div className="action-right">
                <div className="final-price">
                  <span className="price-label">Final Price</span>
                  <p className="price-value">${(selectedCountry.price / 100).toFixed(2)}</p>
                </div>
                <button
                  className="receive-btn"
                  onClick={() => handleReceiveSMS()}
                  disabled={renting}
                >
                  {renting ? (
                    <>
                      <span className="btn-spinner"></span>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>Receive SMS</span>
                      <span className="material-symbols-outlined">bolt</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Services;
